import { probeGetSample, updateFormData } from 'js/actions';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { useGetPaging } from 'js/hooks/useGetPaging';
import { cloneDeep, filter, has, pick } from 'lodash';
import { FeedField } from 'new/components/flows/types/elt/CreateELTFlow/types';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

interface UseLoadFieldsResult {
  loadFields: () => Promise<void>;
  isLoading: boolean;
  fields: any[];
  error: Error | null;
}

export default function useLoadFields(formType, feedValue): UseLoadFieldsResult {
  const { metaInfoById } = useGetPaging();
  const dispatch = useAppDispatch();
  const vendors = useSelector((state: any) => state.entities.vendors);
  const vendorEndpoints = useSelector((state: any) => state.entities.vendorEndpoints);
  const vendorEndpointParams = useSelector((state: any) => state.entities.vendorEndpointParams);
  const selectedConnection = useSelector(
    (state: any) => state.forms[formType]?.data?.selectedConnection,
  );
  const credentialId = useSelector(
    (state: any) =>
      state.forms[formType]?.data?.credentialId ||
      state.forms[formType]?.data?.selectedCredentialId,
  );
  const formData = useSelector((state: any) => state.forms[formType]?.data);
  const isLoading = useSelector(
    (state: any) => state.forms[formType]?.data?.feedFieldsLoading?.[feedValue],
  );
  const [error, setError] = useState<Error | null>(null);

  const loadFields = async () => {
    if (!selectedConnection) {
      setError(new Error('No connection selected'));
      return;
    }

    const { vendorId } = metaInfoById[selectedConnection] || {};
    if (!vendorId) {
      setError(new Error('No vendor ID found'));
      return;
    }
    const endpoints = pick(vendorEndpoints, vendors[vendorId].vendor_endpoints);
    const discoveryFieldsEndpoint = filter(
      endpoints,
      ({ source_template }) => source_template.endpoint_type === 'discovery_fields',
    )[0];
    if (!discoveryFieldsEndpoint) {
      setError(new Error('No discovery_fields endpoint found'));
      return;
    }

    dispatch(updateFormData(formType, { feedFieldsLoading: { [feedValue]: true } }));
    setError(null);

    try {
      const spec = discoveryFieldsEndpoint.source_template.spec;
      let clonedSpec = cloneDeep(spec);
      const resourceParamId = discoveryFieldsEndpoint.resource_parameters?.[0];
      const resourceParam = resourceParamId ? vendorEndpointParams[resourceParamId] : null;

      if (resourceParam) {
        // Recursive function to replace all instances of ${${resourceParam.name}} in an object
        const replaceResourceParam = (obj) => {
          if (typeof obj !== 'object' || obj === null) {
            return obj;
          }

          if (Array.isArray(obj)) {
            return obj.map((item) => replaceResourceParam(item));
          }

          const result = {};
          for (const [key, value] of Object.entries(obj)) {
            if (typeof value === 'string') {
              result[key] = value.replace(`\${${resourceParam.name}}`, feedValue);
            } else {
              result[key] = replaceResourceParam(value);
            }
          }
          return result;
        };

        clonedSpec = replaceResourceParam(clonedSpec);
      }

      const { result } = await dispatch(probeGetSample(credentialId, 0, clonedSpec));
      const { output } = result;

      if (output.response && typeof output.response === 'string') {
        dispatch(
          updateFormData(formType, {
            feedFields: {
              ...formData?.feedFields,
              [feedValue]: JSON.parse(output.response).fields,
            },
          }),
        );
      } else {
        setError(new Error('Failed to load fields'));
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Unknown error occurred'));
    } finally {
      dispatch(updateFormData(formType, { feedFieldsLoading: { [feedValue]: false } }));
    }
  };

  return {
    loadFields,
    isLoading,
    fields: formData?.feedFields?.[feedValue] || ([] as FeedField[]),
    error,
  };
}
